import FileUpload from "@iewccouk/MobiusOverrides/FileUpload/FileUpload";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import setIsBadFile from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetIsBadFile";
import setUploadLimitExceeded from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetUploadLimitExceeded";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    heading = "heading",
    copy = "copy",
    labelOne = "labelOne",
    labelTwo = "labelTwo",
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.copy]: string;
        [fields.labelOne]: string;
        [fields.labelTwo]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const fieldName1 = "fileUpload1";
    const fieldLabelName1 = "fileUploadName1";
    const fieldName2 = "fileUpload2";
    const fieldLabelName2 = "fileUploadName2";
    return {
        fieldName1,
        fieldLabelName1,
        fileUpload1: state.components.contactUsForm.fieldValues[fieldName1],
        fieldName2,
        fieldLabelName2,
        fileUpload2: state.components.contactUsForm.fieldValues[fieldName2],
        state,
    };
};

const mapDispatchToProps = {
    setFieldValue,
    setIsBadFile,
    setUploadLimitExceeded,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface FormFieldStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    fileUploadLabel?: TypographyPresentationProps;
}

export const formFieldStyles: FormFieldStyles = {
    container: {
        gap: 20,
    },
    gridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    headingText: {
        weight: 700,
        size: "18px",
    },
    fileUploadLabel: {
        weight: 700,
        css: css`
            margin-bottom: 10px;
        `,
    },
};

const styles = formFieldStyles;

const UploadInformationWidget: React.FC<Props> = ({
    fields,
    fieldName1,
    fieldLabelName1,
    fileUpload1,
    fieldName2,
    fieldLabelName2,
    fileUpload2,
    setFieldValue,
    setIsBadFile,
    setUploadLimitExceeded,
    state,
}) => {
    const { heading, copy, labelOne, labelTwo } = fields;
    const [fileOneErrorMessage, setFileOneErrorMessagee] = React.useState("");
    const [fileTwoErrorMessage, setFileTwoErrorMessagee] = React.useState("");
    const fileUploadRef = React.useRef({ value: "" } as HTMLInputElement);
    const [fileOneField, setFileOneField] = React.useState<any>(null);
    const [fileTwoField, setFileTwoField] = React.useState<any>(null);
    const [isIncorrectFileOneSize, setIsIncorrectFileOneSize] = React.useState(false);
    const [isIncorrectFileTwoSize, setIsIncorrectFileTwoSize] = React.useState(false);
    const [isIncorrectFileOneExtension, setIsIncorrectFileOneExtension] = React.useState(false);
    const [isIncorrectFileTwoExtension, setIsIncorrectFileTwoExtension] = React.useState(false);
    const contactFormContext = React.useContext(ContactFormContext);

    React.useEffect(() => {
        contactFormContext.validators[fieldName1] = validateFileUploadedFieldOne;
        contactFormContext.validators[fieldName2] = validateFileUploadedFieldTwo;

        if (typeof fileUpload1 !== "undefined") {
            validateFileUploadedFieldOne();
        }

        if (typeof fileUpload2 !== "undefined") {
            validateFileUploadedFieldTwo();
        }
    }, [fileUpload1, fileUpload2]);

    React.useEffect(() => {
        if (fileOneField) {
            setIsIncorrectFileOneExtension(getFileExtension(fileOneField.name)?.toLowerCase?.() !== "pdf");
            setIsIncorrectFileOneSize(fileOneField.size > 3e6);
        }
    }, [fileOneField]);

    React.useEffect(() => {
        if (fileTwoField) {
            setIsIncorrectFileTwoExtension(getFileExtension(fileTwoField.name)?.toLowerCase?.() !== "pdf");
            setIsIncorrectFileTwoSize(fileTwoField.size > 3e6);
        }
    }, [fileTwoField]);

    const validate = (file: any, isIncorrectSize: any, isIncorrectExtension: any) => {
        let errorMessage = "";

        if (file && isIncorrectExtension) {
            errorMessage = translate("File type must be PDF");
            setIsBadFile({ isBadFile: true });
        } else if (file && isIncorrectSize) {
            errorMessage = translate("File size exceeds 3MB");
            setUploadLimitExceeded({ uploadLimitExceeded: true });
        }

        return errorMessage;
    };

    const validateFileUploadedFieldOne = () => {
        const errorMessage = validate(fileOneField, isIncorrectFileOneSize, isIncorrectFileOneExtension);
        setFileOneErrorMessagee(errorMessage as any);
        return !errorMessage;
    };

    const validateFileUploadedFieldTwo = () => {
        const errorMessage = validate(fileTwoField, isIncorrectFileTwoSize, isIncorrectFileTwoExtension);
        setFileTwoErrorMessagee(errorMessage as any);
        return !errorMessage;
    };

    const readFile = (file: any, fieldName: string, fieldLabelName: string) => {
        const myReader: FileReader = new FileReader();
        myReader.onloadend = e => {
            setFieldValue({ key: fieldName, value: myReader.result as string });
            setFieldValue({ key: fieldLabelName, value: file.name });
        };
        myReader.readAsDataURL(file);
    };

    const handleFileChange = (event: any, fieldName: string, fieldLabelName: string, isFirst = true) => {
        const file = event.target.files?.[0];
        if (file) {
            readFile(file, fieldName, fieldLabelName);
        } else {
            setFieldValue({ key: fieldName, value: "" });
            setFieldValue({ key: fieldLabelName, value: "" });
        }

        if (isFirst) {
            setFileOneField(file ?? "");
        } else {
            setFileTwoField(file ?? "");
        }
    };

    // VERNDALE CUSTOMIZATION START - watch session storage key clearUploadFiles to clear the uploaded files and remove the key
    React.useEffect(() => {
        if (sessionStorage.getItem("clearUploadFiles") === "true") {
            setFileOneField(null);
            setFileTwoField(null);
            setFieldValue({ key: fieldName1, value: "" });
            setFieldValue({ key: fieldLabelName1, value: "" });
            setFieldValue({ key: fieldName2, value: "" });
            setFieldValue({ key: fieldLabelName2, value: "" });
            sessionStorage.removeItem("clearUploadFiles");
        }
    }, [state.components.contactUsForm.fieldValues]);
    // VERNDALE CUSTOMIZATION START - watch session storage key clearUploadFiles to clear the uploaded files and remove the key

    const buttonTextLabel = translate("Add a file");
    const placeholderLabel = translate("No file selected");

    return (
        <>
            <GridContainer {...styles.container}>
                <GridItem {...styles.gridItem}>
                    <Typography {...styles.headingText}>{heading}</Typography>
                </GridItem>
                <GridItem {...styles.gridItem}>
                    <StyledWrapper>{parse(copy, parserOptions)}</StyledWrapper>
                </GridItem>
                <GridItem {...styles.gridItem}>
                    <Typography {...styles.fileUploadLabel}>{labelOne}</Typography>
                    <FileUpload
                        inputRef={fileUploadRef}
                        fileName={fileOneField?.name || ""}
                        accept=".pdf"
                        error={fileOneErrorMessage}
                        onFileChange={e => handleFileChange(e, fieldName1, fieldLabelName1)}
                        buttonText={buttonTextLabel}
                        placeholder={placeholderLabel}
                    />
                </GridItem>
                <GridItem {...styles.gridItem}>
                    <Typography {...styles.fileUploadLabel}>{labelTwo}</Typography>
                    <FileUpload
                        inputRef={fileUploadRef}
                        fileName={fileTwoField?.name || ""}
                        accept=".pdf"
                        error={fileTwoErrorMessage}
                        onFileChange={e => handleFileChange(e, fieldName2, fieldLabelName2, false)}
                        buttonText={buttonTextLabel}
                        placeholder={placeholderLabel}
                    />
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(UploadInformationWidget as any) as any,
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Upload Spec/Additional Information",
                isRequired: true,
            },
            {
                name: fields.copy,
                displayName: "Copy",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue:
                    "Have a spec? Attach a PDF to your contact us request by clicking CHOOSE FILE below. <i>(maximum size of 3MB)</i>",
                isRequired: true,
            },
            {
                name: fields.labelOne,
                displayName: "Upload field 1 label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Upload file 1",
                isRequired: true,
            },
            {
                name: fields.labelTwo,
                displayName: "Upload field 2 label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Upload file 2",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
